import React, {useEffect, useState} from "react";
import {
    Column,
    ColumnType,
    FixedSelectionFilter,
    FreeTextFilter,
    GetParams,
    GlobalActionType,
    KButton,
    KBUTTON_TYPE,
    KBUTTON_VARIANT,
    KCard,
    KCheck,
    KContainer,
    KForm,
    KInput,
    KModal,
    KModalBackdrop,
    KModalSize,
    KSelect,
    KSELECT_BACKCOLOR,
    KSELECT_SIZE,
    KSpace,
    KSpinner,
    KSpinnerSize,
    KTableLoader,
    Table
} from "@kopjra/uikit";
import {Paginated, Stored, tableGetParams} from "../utils/commons";
import {
    Archetype,
    ArchetypeCreation,
    Callback,
    CallbackEvent,
    CallbackResourceType, emailCustomisationSchema,
    signatureCustomisationsSchema
} from "../types/users";
import {BuyCTA} from "./helpers/BuyCTA";
import {I18n, Translate} from "react-redux-i18n";
import {Col, Row} from "react-bootstrap";
import {JsonEditor} from "./helpers/JsonEditor";
import ReactJson from "react-json-view";

export interface StateProps {
    callbacks?: Paginated<Stored<Callback>>;
    archetypes?: Paginated<Stored<Archetype>>;
    svixAppPortalUrl?: string;
    isAllowed: boolean;
}

export interface DispatchProps {
    onGetCallbacks: (q: GetParams) => Promise<void>;
    onCreateCallback: (call: Callback, q: GetParams) => Promise<void>;
    onGetArchetypes: (q: GetParams) => Promise<void>;
    onCreateArchetype: (arch: ArchetypeCreation, q: GetParams) => Promise<void>;
    onDeleteArchetype: (id: string, q: GetParams) => Promise<void>;
    onDeleteCallback: (id: string, q: GetParams) => Promise<void>;
}

export interface InnerProps {}

export type Props = StateProps & DispatchProps & InnerProps;

const archTableName = "archetypes";
const callTableName = "callbacks";

export const ArchetypesCallbacks: React.FC<Props> = ({svixAppPortalUrl, onDeleteCallback, onDeleteArchetype, onCreateArchetype, archetypes, onGetArchetypes, onCreateCallback, isAllowed, callbacks, onGetCallbacks}) => {
    useEffect(() => {
        (async () => {
            if (!callbacks) {
                await onGetCallbacks(tableGetParams(callTableName));
            }
            if (!archetypes) {
                await onGetArchetypes(tableGetParams(archTableName));
            }
        })().catch(console.log);
    }, [onGetCallbacks, onGetArchetypes, callbacks, archetypes]);

    const [createCall, setCreateCall] = useState(false);
    const [createCallSubmitting, setCreateCallSubmitting] = useState(false);
    const [showCustom, setShowCustom] = useState<object | undefined>();

    const [createArch, setCreateArch] = useState(false);
    const [createArchSubmitting, setCreateArchSubmitting] = useState(false);

    const forceBuy = !isAllowed && callbacks && callbacks.total === 0 && archetypes && archetypes.total === 0;

    // TODO: Gestire la validazione e il dato pronto di Monaco

    const globalActions: GlobalActionType[] = [
        {
            name: <><i className={"fal fa-plus fag"}/> <Translate value={"generic.create"}/></>,
            handler: async () => {setCreateCall(true)},
        },
    ];

    if (svixAppPortalUrl) {
        globalActions.push({
            name: <><i className="fal fag fa-eye"/> <Translate value={"callbacks.svix"}/></>,
            handler: async () => {window.open(svixAppPortalUrl, "_blank")},
        });
    }

    return (
        <KContainer className={"text-center"}>
            {forceBuy ? <BuyCTA/> : (
                <>
                    <KCard header={<Translate value={"callbacks.title"}/>}>
                        {callbacks ? (
                            <Table
                                id={callTableName}
                                loaderFunc={(q) => onGetCallbacks(q)}
                                data={callbacks.items}
                                total_count={callbacks.total}
                                keyField={"id"}
                                waiter={<KSpinner size={KSpinnerSize.xl}/>}
                                filterDefinition={[
                                    new FixedSelectionFilter(
                                        "resourceType",
                                        <Translate value={"callbacks.table.resourceType"}/>,
                                        Object.values(CallbackResourceType).map(value => ({
                                            label: I18n.t(`callbacks.table.${value}` as any),
                                            value,
                                        }))
                                    ),
                                    new FreeTextFilter("endpoint", <Translate value={"callbacks.table.endpoint"}/>),
                                ]}
                                globalActions={globalActions}
                            >
                                <Column colid="id" colspan={1} type={ColumnType.GENERIC} name="ID" sort="id" classes="text-left" visible={false}/>
                                <Column colid="resourceType" colspan={2} type={ColumnType.GENERIC} name="callbacks.table.resourceType" sort="resourceType" classes="text-left"
                                        render={(datum: Callback) => <Translate value={`callbacks.table.${datum.resourceType}` as any}/>}
                                />
                                <Column colid="endpoint" colspan={4} type={ColumnType.GENERIC} name="callbacks.table.endpoint" sort="endpoint" classes="text-left"/>
                                <Column colid="onlyFinalStatuses" colspan={2} type={ColumnType.GENERIC} name="callbacks.table.onlyFinalStatuses" sort="onlyFinalStatuses" classes="text-left"
                                        render={(datum: Callback) => <Translate value={datum.onlyFinalStatuses ? "generic.yes" : "generic.no"}/>}
                                />
                                <Column colid="events" colspan={2} type={ColumnType.GENERIC} name="callbacks.table.events" sort="events" classes="text-left"
                                        render={(datum: Callback) => datum.events?.map((value) => I18n.t(`callbacks.table.${value}` as any)).join(", ") || <></>}
                                />
                                <Column colid="actions" colspan={2} type={ColumnType.ACTIONS} actions={[
                                    {
                                        name: <i className="fal fa-trash action-icon"/>,
                                        handler: async ({id}: Stored<Callback>) => onDeleteCallback(id, tableGetParams(callTableName)),
                                        confirmation: true,
                                    }
                                ]}/>
                            </Table>
                        ) : <KTableLoader/>}
                    </KCard>
                    <KSpace/>
                    <KCard header={<Translate value={"archetypes.title"}/>}>
                        {archetypes ? (
                            <Table
                                id={archTableName}
                                loaderFunc={(q) => onGetArchetypes(q)}
                                data={archetypes.items}
                                total_count={archetypes.total}
                                keyField={"id"}
                                waiter={<KSpinner size={KSpinnerSize.xl}/>}
                                filterDefinition={[
                                    new FreeTextFilter("name", <Translate value={"archetypes.table.name"}/>),
                                ]}
                                globalActions={[
                                    {
                                        name: <><i className={"fal fa-plus"}/> <Translate value={"generic.create"}/></>,
                                        handler: async () => {setCreateArch(true)},
                                    }
                                ]}
                            >
                                <Column colid="id" colspan={1} type={ColumnType.GENERIC} name="ID" sort="id" classes="text-left"/>
                                <Column colid="name" colspan={2} type={ColumnType.GENERIC} name="archetypes.table.name" sort="name" classes="text-left"/>
                                <Column colid="mandatoryTags" colspan={2} type={ColumnType.GENERIC} name="archetypes.table.mandatoryTags" classes="text-left"
                                        render={(datum: Archetype) => datum.mandatoryTags ? Object.keys(datum.mandatoryTags).join(", ") : <></>}
                                />
                                <Column colid="formTags" colspan={2} type={ColumnType.GENERIC} name="archetypes.table.formTags" classes="text-left"
                                        render={(datum: Archetype) => datum.formTags ? Object.keys(datum.formTags).join(", ") : <></>}
                                />
                                <Column colid="smsCustomMessage" colspan={2} type={ColumnType.GENERIC} name="archetypes.table.smsCustomMessage" sort="smsCustomMessage" classes="text-left"/>
                                <Column colid="smsCustomMessageModeratedOn" colspan={2} type={ColumnType.DATETIME} name="archetypes.table.smsCustomMessageModeratedOn" sort="smsCustomMessageModeratedOn" classes="text-left"/>
                                <Column colid="actions" colspan={2} type={ColumnType.ACTIONS} actions={[
                                    {
                                        name: <i className="fal fa-sliders-v-square action-icon"/>,
                                        handler: async (datum: Archetype) => setShowCustom(datum.signatureCustomisations),
                                        shouldRender: (datum: Archetype) => !!datum.signatureCustomisations,
                                        tooltip: <Translate value={"archetypes.table.signatureCustomisations"}/>,
                                    }, {
                                        name: <i className="fal fa-square-envelope action-icon"/>,
                                        handler: async (datum: Archetype) => setShowCustom(datum.emailCustomisation),
                                        shouldRender: (datum: Archetype) => !!datum.emailCustomisation,
                                        tooltip: <Translate value={"archetypes.table.emailCustomisations"}/>,
                                    }, {
                                        name: <i className="fal fa-trash action-icon" />,
                                        handler: async ({id}: Stored<Archetype>) => onDeleteArchetype(id, tableGetParams(archTableName)),
                                        confirmation: true,
                                    }
                                ]}/>
                            </Table>
                        ) : <KTableLoader/>}
                    </KCard>
                </>
            )}
            <KModal show={!!showCustom} size={KModalSize.lg} header={<Translate value={"archetypes.table.openCustomisations"}/>} onHide={() => setShowCustom(undefined)}>
                {showCustom ? (
                    <div style={{width: "100%", overflowX: "auto"}}>
                        <ReactJson src={showCustom} collapsed={1} collapseStringsAfterLength={15} displayObjectSize={false}/>
                    </div>
                ) : null}
            </KModal>
            <KModal show={createCall} header={<Translate value={"callbacks.create"}/>} onHide={() => setCreateCall(false)} footer={<></>} backdrop={KModalBackdrop.static}>
                <KForm onSubmit={async (values) => {
                            await onCreateCallback({
                                ...(values as any),
                                onlyFinalStatuses: !!values.onlyFinalStatuses,
                                events: values.events ? JSON.parse(values.events as string) : undefined,
                            }, tableGetParams(callTableName));
                            setCreateCall(false);
                        }}
                       onSubmitting={submitting => setCreateCallSubmitting(submitting)}
                >
                    <Row>
                        <Col>
                            <KSelect id={"resourceType"}
                                     label={<Translate value={"callbacks.table.resourceType"}/>}
                                     options={Object.values(CallbackResourceType).map(value => ({
                                         label: <Translate value={`callbacks.table.${value}` as any}/>,
                                         value,
                                     }))}
                                     backColor={KSELECT_BACKCOLOR.gray}
                                     underline={true}
                                     size={KSELECT_SIZE.lg}
                                     required={true}
                                     errorMessage={I18n.t("generic.required")}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <KInput id={"endpoint"} label={<Translate value={"callbacks.table.endpoint"}/>} required={true} type={"url"}
                                    errorMessage={new Map([
                                        ["required", <Translate value={"generic.required"}/>],
                                        ["type", <Translate value={"generic.badUrl"}/>],
                                    ])}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <KSelect id={"events"}
                                     label={<Translate value={"callbacks.table.events"}/>}
                                     options={Object.values(CallbackEvent).map(value => ({
                                         label: <Translate value={`callbacks.table.${value}` as any}/>,
                                         value,
                                     }))}
                                     backColor={KSELECT_BACKCOLOR.gray}
                                     underline={true}
                                     size={KSELECT_SIZE.lg}
                                     clearable={true}
                                     multi={true}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <KCheck id={"onlyFinalStatuses"} label={<Translate value={"callbacks.table.onlyFinalStatuses"}/>}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6}>
                            <KButton text={createCallSubmitting ? <KSpinner/> : <Translate value={"generic.create"}/>} type={KBUTTON_TYPE.submit} variant={KBUTTON_VARIANT.primary}
                                     disabled={createCallSubmitting} fill={true}
                            />
                        </Col>
                        <Col sm={6}>
                            <KButton text={<Translate value={"generic.close"}/>} type={KBUTTON_TYPE.button} variant={KBUTTON_VARIANT.secondary}
                                     onClick={() => setCreateCall(false)}
                                     disabled={createCallSubmitting} fill={true}
                            />
                        </Col>
                    </Row>
                </KForm>
            </KModal>
            <KModal show={createArch} header={<Translate value={"archetypes.create"}/>} onHide={() => setCreateArch(false)} footer={<></>} backdrop={KModalBackdrop.static}>
                <KForm onSubmit={async (values) => {
                            await onCreateArchetype({
                                ...(values as any),
                                mandatoryTags: (values.mandatoryTags as string)?.split(",").reduce((previousValue, currentValue) => ({...previousValue, [currentValue.trim()]: null}), {}),
                                formTags: (values.formTags as string)?.split(",").reduce((previousValue, currentValue) => ({...previousValue, [`form$${currentValue.trim()}`]: null}), {}),
                                signatureCustomisations: values.signatureCustomisations ? JSON.parse(values.signatureCustomisations as string) : undefined,
                                emailCustomisation: values.emailCustomisation ? JSON.parse(values.emailCustomisation as string) : undefined,
                            }, tableGetParams(archTableName));
                            setCreateArch(false);
                        }}
                       onSubmitting={submitting => setCreateArchSubmitting(submitting)}
                >
                    <Row>
                        <Col>
                            <KInput id={"name"} label={<Translate value={"archetypes.table.name"}/>}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <KInput id={"smsCustomMessage"} label={<Translate value={"archetypes.table.smsCustomMessage"}/>}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <KInput id={"mandatoryTags"} label={<Translate value={"archetypes.table.mandatoryTags"}/>} hint={<Translate value={"archetypes.hintTag"}/>}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <KInput id={"formTags"} label={<Translate value={"archetypes.table.formTags"}/>} hint={<Translate value={"archetypes.hintTag"}/>}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <JsonEditor id={"signatureCustomisations"} label={<Translate value={"archetypes.table.signatureCustomisations"}/>} schema={signatureCustomisationsSchema}
                                        errorMessage={new Map().set("required", <Translate value="generic.required"/>).set("json", <Translate value="generic.badJson"/>)}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <JsonEditor id={"emailCustomisation"} label={<Translate value={"archetypes.table.emailCustomisations"}/>} schema={emailCustomisationSchema}
                                        errorMessage={new Map().set("required", <Translate value="generic.required"/>).set("json", <Translate value="generic.badJson"/>)}/>
                        </Col>
                    </Row>
                    <KSpace spaces={2}/>
                    <Row>
                        <Col sm={6}>
                            <KButton text={createArchSubmitting ? <KSpinner/> : <Translate value={"generic.create"}/>} type={KBUTTON_TYPE.submit} variant={KBUTTON_VARIANT.primary}
                                     disabled={createArchSubmitting} fill={true}
                            />
                        </Col>
                        <Col sm={6}>
                            <KButton text={<Translate value={"generic.close"}/>} type={KBUTTON_TYPE.button} variant={KBUTTON_VARIANT.secondary}
                                     onClick={() => setCreateArch(false)}
                                     disabled={createArchSubmitting} fill={true}
                            />
                        </Col>
                    </Row>
                </KForm>
            </KModal>
        </KContainer>
    )
}
